/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { X, AlertCircle, CheckCircle, Info, AlertTriangle } from "lucide-react";
import { memo } from "react";
import {
  Toast,
  ToastProvider,
  ToastViewport,
  ToastTitle,
  ToastDescription,
  ToastAction,
  ToastClose,
} from "@atomic/ui-kit";
import { useSystemMessages } from "../hooks/useSystemMessages";
import type { SystemMessageType } from "../contexts/SystemMessagesContext";
import { theme } from "../styles/theme/theme";

/**
 * Renders the app's system messages as @atomic/ui-kit Toasts (Radix under the
 * hood): accessible, swipe-dismissable, animated by the kit. This is only the
 * presentational renderer — the message list, ids and auto-dismiss TTL still
 * live in SystemMessagesContext, and every caller keeps using `addMessage`
 * unchanged. Auto-dismiss is owned by the context (its setTimeout), so each
 * Toast runs at `duration={Infinity}` and we map a manual/swipe close
 * (`onOpenChange(false)`) back to `removeMessage`.
 */

const messageTypeIcons: Record<SystemMessageType, typeof AlertCircle> = {
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
  success: CheckCircle,
};

const messageTypeColors: Record<
  SystemMessageType,
  { bg: string; border: string; text: string; icon: string }
> = {
  error: {
    bg: theme.colors.statusErrorSoft,
    border: theme.colors.statusError,
    text: theme.colors.statusErrorForeground,
    icon: theme.colors.statusError,
  },
  warning: {
    bg: theme.colors.statusWarningSoft,
    border: theme.colors.statusWarning,
    text: theme.colors.statusWarningForeground,
    icon: theme.colors.statusWarning,
  },
  info: {
    // No statusInfoSoft/base token exists; primarySoft (the blue tint) is the
    // closest soft surface for "info", with statusInfoForeground for ink.
    bg: theme.colors.primarySoft,
    border: theme.colors.primarySoft,
    text: theme.colors.statusInfoForeground,
    icon: theme.colors.statusInfoForeground,
  },
  success: {
    bg: theme.colors.statusSuccessSoft,
    border: theme.colors.statusSuccess,
    text: theme.colors.statusSuccessForeground,
    icon: theme.colors.statusSuccess,
  },
};

const SystemMessages = memo(() => {
  const { messages, removeMessage } = useSystemMessages();

  return (
    <ToastProvider duration={Infinity} swipeDirection="right">
      {messages.map((message) => {
        const Icon = messageTypeIcons[message.type];
        const colors = messageTypeColors[message.type];

        return (
          <Toast
            key={message.id}
            open
            duration={Infinity}
            onOpenChange={(open) => {
              if (!open) removeMessage(message.id);
            }}
            css={css`
              background-color: ${colors.bg};
              border: 1px solid ${colors.border};
              display: flex;
              gap: 0.75rem;
              align-items: flex-start;
            `}
          >
            <Icon
              css={css`
                width: 1.25rem;
                height: 1.25rem;
                flex-shrink: 0;
                margin-top: 0.125rem;
                color: ${colors.icon};
              `}
            />
            <div
              css={css`
                flex: 1;
                min-width: 0;
              `}
            >
              <ToastTitle
                css={css`
                  font-weight: 600;
                  font-size: 0.875rem;
                  color: ${colors.text};
                `}
              >
                {message.title}
              </ToastTitle>
              {message.description && (
                <ToastDescription
                  css={css`
                    font-size: 0.8125rem;
                    color: ${colors.text};
                    opacity: 0.9;
                    word-wrap: break-word;
                  `}
                >
                  {message.description}
                </ToastDescription>
              )}
              {message.actionLabel && message.onAction && (
                <ToastAction
                  altText={message.actionLabel}
                  onClick={() => {
                    message.onAction?.(message.id);
                    removeMessage(message.id);
                  }}
                  css={css`
                    margin-top: 0.5rem;
                    font-size: 0.8125rem;
                    font-weight: 500;
                    color: ${colors.icon};
                    background: none;
                    border: 1px solid ${colors.border};
                    border-radius: ${theme.borderRadius.sm};
                    padding: 0.25rem 0.5rem;
                    cursor: pointer;
                    &:hover {
                      background-color: ${theme.colors.hoverSurface};
                    }
                  `}
                >
                  {message.actionLabel}
                </ToastAction>
              )}
            </div>
            <ToastClose
              aria-label="Dismiss message"
              css={css`
                color: ${colors.text};
                opacity: 0.7;
                flex-shrink: 0;
                &:hover {
                  opacity: 1;
                  background-color: ${theme.colors.hoverSurface};
                }
              `}
            >
              <X
                css={css`
                  width: 1rem;
                  height: 1rem;
                `}
              />
            </ToastClose>
          </Toast>
        );
      })}
      <ToastViewport
        css={css`
          position: fixed;
          bottom: 1rem;
          right: 1rem;
          z-index: 9999;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          width: auto;
          max-width: 28rem;
          margin: 0;
          padding: 0;
          list-style: none;
          /* The fixed viewport spans a region even when empty — keep it
             click-through so it never swallows page clicks. Each kit Toast sets
             pointer-events: auto, so open toasts stay interactive. */
          pointer-events: none;
        `}
      />
    </ToastProvider>
  );
});

SystemMessages.displayName = "SystemMessages";

export default SystemMessages;
